/*
 * HOME4HEROES — card swatch pilot.  OM55 §5d, owner exception 2026-09-15.
 *
 * SCOPE, DELIBERATELY NARROW
 * --------------------------
 * The owner granted a Phase 3 slice for swatch behaviour **limited to the 25 RED
 * Friday products**, so this file does nothing anywhere else. It activates only
 * on the RED Friday collection route. Everything Ella does on every other page
 * is untouched.
 *
 * WHY IT EXISTS: Ella's own handler cannot work
 * --------------------------------------------
 * `assets/global.js` ColorSwatch.handleSwatchClick updates the title, the price
 * and the heading href, and then tries to swap the card image. That last part is
 * broken four separate ways, so the image never changes — which is exactly what
 * the owner reported:
 *
 *   1. `const mediaList = productJson.media.filter(...)` is declared with `const`
 *      INSIDE an `if` block, shadowing the outer `mediaList = []`. The outer one
 *      stays empty, so the code always falls to the `else` branch.
 *   2. that same filter is written `(index, element) => element.alt === title`,
 *      but `Array.filter` passes `(element, index)` — so `element` is a number
 *      and `element.alt` is always `undefined`.
 *   3. `const length = 2` and `const length = mediaList.length` are declared
 *      inside `if`/`else` blocks, so `length` is out of scope in the `for` that
 *      follows.
 *   4. `querySelector(".card__media img:eq(0)")` uses jQuery's `:eq()`, which is
 *      not CSS — `querySelector` throws `SyntaxError` on it.
 *
 * Verified on the rendered RED Friday collection, 2026-09-15: clicking a swatch
 * changed the title to "… - Dark Heather" and left the photograph on the
 * original colour.
 *
 * WHAT THIS DOES INSTEAD
 * ----------------------
 * The card already carries everything needed. Its `<template>` holds the full
 * product JSON, and on this collection every variant has a `featured_image`
 * (verified: 78 of 78 on the reference product). So: read the variant by id,
 * set the primary image AND the hover image from it, and point every link on the
 * card at that variant.
 *
 * The hover image matters. Ella never touches `media[1]`, so after a swatch click
 * the card used to revert to the original colour the moment the pointer moved
 * over it.
 *
 * ACCESSIBILITY (WCAG 2.2 AA)
 * ---------------------------
 * Ella renders swatches as bare `<span>` inside `<li>` — no `href`, no `role`,
 * no `tabindex`, no accessible name — and hides their tooltip below 1025px. They
 * are unusable by keyboard and silent to a screen reader. This file promotes each
 * one to a real radio-group control with a name, and drives it from the keyboard.
 * That is new markup, not repair, and it is why it is scoped to the pilot.
 */
(function () {
  'use strict';

  /* STORE-WIDE since 2026-09-21 (file 25 §2, owner decision).
     This began as a pilot fenced to `/collections/red-friday` by
     `OWNER_MESSAGE_55.md` §5, AS AN EXPERIMENT. The experiment ran, was measured
     good, and the owner graduated it. File 25 §1 also corrects the reason file 24
     gave for keeping the fence: this file is theme-side JavaScript and writes NO
     product data, so the product-data limit never applied to it. Catalogue writes
     - including linking media for the 18 link-only products - remain closed.

     Kept as a named constant rather than deleted, because re-fencing is then one
     line: return PILOT_ROUTE.test(...) again. */
  var PILOT_ROUTE = /\/collections\/red-friday(\/|$|\?)/;
  var STORE_WIDE = true;

  function inScope() {
    if (STORE_WIDE) return true;
    return PILOT_ROUTE.test(window.location.pathname + window.location.search);
  }

  /* the card's own product JSON, parsed once and cached on the element */
  function productOf(card) {
    if (card.__h4hProduct !== undefined) return card.__h4hProduct;
    var parsed = null;
    try {
      var tpl = card.querySelector('template');
      if (tpl && tpl.content) {
        var holder = tpl.content.querySelector('[data-json-product]');
        if (holder) parsed = JSON.parse(holder.getAttribute('data-json-product'));
      }
    } catch (e) {
      parsed = null;
    }
    card.__h4hProduct = parsed;
    return parsed;
  }

  function imagesOf(card) {
    var media = card.querySelector('.card__media') || card;
    return Array.prototype.slice.call(media.querySelectorAll('img'));
  }

  /* STEP 5 - the card renders at ~260 px and `variant.featured_image.src` is the
     FULL-SIZE master. The first version of this function wrote that URL into
     `srcset` with no width descriptor, which makes it the browser's only
     candidate and leaves the inherited `sizes` with nothing to choose between.
     Measured on the preview before the fix: a 2000x2000 JPEG decoded into a
     260x260 box on every swatch click. Rebuild a real descriptor set instead,
     and leave `sizes` alone - it is Ella's own and already matches the grid. */
  var SWATCH_WIDTHS = [180, 260, 360, 480, 720, 960];

  function cdnWidth(src, w) {
    var base = String(src).split('&width=')[0].split('?width=')[0];
    return base + (base.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  }

  function applyImage(img, src) {
    if (!img || !src) return;
    var set = [];
    for (var i = 0; i < SWATCH_WIDTHS.length; i++) {
      set.push(cdnWidth(src, SWATCH_WIDTHS[i]) + ' ' + SWATCH_WIDTHS[i] + 'w');
    }
    img.setAttribute('src', cdnWidth(src, 480));
    img.setAttribute('srcset', set.join(', '));
    img.removeAttribute('data-srcset');
  }

  /* STEP 5 - the card's embedded product JSON carries only id, handle, media and
     variants: there is NO `title` key (verified on the rendered RED Friday grid,
     21 of 21 cards). The first version read `product.title`, so every radiogroup
     announced "Colour, product" and a swapped image's alt collapsed to the bare
     colour name, e.g. "Dark Heather", losing the product entirely. The title is
     still recoverable two ways, both present on every card. */
  function titleOf(card, product) {
    if (product && product.title) return String(product.title).trim();
    var link = card.querySelector('.card__title a, a.card__title, h3 a, a[href*="/products/"]');
    var text = link && link.textContent ? link.textContent.replace(/\s+/g, ' ').trim() : '';
    if (text) return text;
    /* last resort: variant.name is "<product title> - <option>" */
    var v = product && product.variants && product.variants[0];
    if (v && v.name) return String(v.name).split(' - ')[0].trim();
    return '';
  }

  function swapTo(card, variantId, colourName) {
    var product = productOf(card);
    if (!product || !product.variants) return false;

    var variant = null;
    for (var i = 0; i < product.variants.length; i++) {
      if (Number(product.variants[i].id) === Number(variantId)) { variant = product.variants[i]; break; }
    }
    if (!variant) return false;

    /* THE LINK FOLLOWS THE COLOUR EVEN WHEN THAT COLOUR HAS NO PHOTOGRAPH, and
       it is set BEFORE the image early-return on purpose. The customer clicked
       navy; the product page must open on navy whether or not a navy photo
       exists. Measured in the file 25 §2.1 degradation run, before this change:
       on a product with no linked media the image correctly stayed put, but the
       card also kept linking to the DEFAULT variant - the one case in the whole
       test where a customer could land on a different colour from the one they
       clicked. Every link on the card, not only the heading. */
    Array.prototype.forEach.call(card.querySelectorAll('a[href*="/products/"]'), function (a) {
      var href = a.getAttribute('href') || '';
      a.setAttribute('href', href.split('?')[0] + '?variant=' + variant.id);
    });

    var featured = variant.featured_image && variant.featured_image.src;
    if (!featured) return false;   /* no photo for this colour: leave the PICTURE alone */

    var imgs = imagesOf(card);
    applyImage(imgs[0], featured);
    /* the hover/secondary image has to follow, or the card reverts on hover */
    if (imgs[1]) applyImage(imgs[1], featured);

    if (colourName) {
      Array.prototype.forEach.call(imgs, function (im) {
        var base = titleOf(card, product);
        im.setAttribute('alt', base ? base + ' — ' + colourName : colourName);
      });
    }
    return true;
  }

  function nameOf(el) {
    var d = el.dataset || {};
    var raw = d.title || el.getAttribute('title') || el.getAttribute('aria-label') || '';
    return String(raw).replace(/-/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); }).trim();
  }

  /* --- make the swatches operable, and announced ------------------------- */
  function upgrade(card) {
    if (card.__h4hSwatchUpgraded) return;
    var list = card.querySelector('.swatch-list');
    if (!list) return;
    card.__h4hSwatchUpgraded = true;

    var product = productOf(card);
    var label = titleOf(card, product);
    list.setAttribute('role', 'radiogroup');
    list.setAttribute('aria-label', label ? 'Colour, ' + label : 'Colour');

    Array.prototype.forEach.call(list.querySelectorAll('.swatch-item'), function (item) {
      var inner = item.querySelector('[data-variant-id]') || item.querySelector('span') || item;
      var name = nameOf(inner) || nameOf(item);
      if (!name) return;
      var active = item.classList.contains('active');
      inner.setAttribute('role', 'radio');
      inner.setAttribute('aria-checked', active ? 'true' : 'false');
      inner.setAttribute('aria-label', name);
      inner.setAttribute('tabindex', active ? '0' : '-1');
    });
  }

  function markSelected(card, chosen) {
    Array.prototype.forEach.call(card.querySelectorAll('.swatch-item'), function (item) {
      var inner = item.querySelector('[data-variant-id]') || item.querySelector('span') || item;
      var on = item === chosen;
      item.classList.toggle('active', on);
      inner.setAttribute('aria-checked', on ? 'true' : 'false');
      inner.setAttribute('tabindex', on ? '0' : '-1');
    });
  }

  function choose(card, item) {
    var inner = item.querySelector('[data-variant-id]') || item.querySelector('span') || item;
    var id = inner.dataset ? inner.dataset.variantId : null;
    if (!id) return;
    markSelected(card, item);
    swapTo(card, id, nameOf(inner));
  }

  function handle(event) {
    if (!inScope()) return;
    var item = event.target.closest && event.target.closest('.swatch-item');
    if (!item) return;
    var card = item.closest('.card-wrapper') || item.closest('[class*="card"]');
    if (!card) return;
    upgrade(card);
    /* let Ella update the title and the price first, then correct the image */
    setTimeout(function () { choose(card, item); }, 0);
  }

  function keys(event) {
    if (!inScope()) return;
    var item = event.target.closest && event.target.closest('.swatch-item');
    if (!item) return;
    var card = item.closest('.card-wrapper');
    if (!card) return;
    var items = Array.prototype.slice.call(card.querySelectorAll('.swatch-item'));
    var i = items.indexOf(item), next = null;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = items[(i + 1) % items.length];
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = items[(i - 1 + items.length) % items.length];
    else if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); choose(card, item); return; }
    if (next) {
      event.preventDefault();
      choose(card, next);
      var inner = next.querySelector('[data-variant-id]') || next.querySelector('span') || next;
      if (inner.focus) inner.focus();
    }
  }

  function upgradeAll() {
    if (!inScope()) return;
    Array.prototype.forEach.call(document.querySelectorAll('.card-wrapper'), upgrade);
  }

  document.addEventListener('click', handle, true);
  document.addEventListener('keydown', keys, true);
  document.addEventListener('DOMContentLoaded', upgradeAll);
  if (document.readyState !== 'loading') upgradeAll();
  /* the grid paginates and filters in place, so re-upgrade when cards arrive.
     DEBOUNCED as of the store-wide switch: this observer watches the whole
     document subtree, and it now runs on EVERY page rather than one collection.
     Lazy images, swiper init and the cart drawer all mutate the tree constantly,
     and each raw callback previously cost a full `querySelectorAll('.card-wrapper')`.
     Coalescing into one pass per frame keeps the behaviour identical - `upgrade()`
     already early-returns on a card it has done - while removing the repeat scans. */
  if (window.MutationObserver) {
    var pending = false;
    new MutationObserver(function () {
      if (pending) return;
      pending = true;
      (window.requestAnimationFrame || window.setTimeout)(function () {
        pending = false;
        upgradeAll();
      }, 0);
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
